import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { resolveWithCredentials } from "./auth-header";
import { normalizeAxiosError } from "./error-map";

type QueryParamPrimitive = string | number | boolean | null | undefined;

export type QueryParams = Record<string, QueryParamPrimitive | QueryParamPrimitive[]>;

export type NetworkHeaders = Record<string, string>;

export interface NetworkRequestOptions {
  headers?: NetworkHeaders;
  pathParams?: Record<string, string | number>;
  queryParams?: QueryParams;
  withCredentials?: boolean;
  useAuth?: boolean;
  signal?: AbortSignal;
}

export interface NetworkBodyRequestOptions extends NetworkRequestOptions {
  body?: unknown;
}

export interface NetworkFormRequestOptions extends NetworkRequestOptions {
  formData: FormData;
}

export interface NetworkAdapter {
  get<T>(endpoint: string, options?: NetworkRequestOptions): Promise<T>;
  post<T>(endpoint: string, options?: NetworkBodyRequestOptions): Promise<T>;
  patch<T>(endpoint: string, options?: NetworkBodyRequestOptions): Promise<T>;
  put<T>(endpoint: string, options?: NetworkBodyRequestOptions): Promise<T>;
  delete<T>(endpoint: string, options?: NetworkRequestOptions): Promise<T>;
  postFormData<T>(endpoint: string, options: NetworkFormRequestOptions): Promise<T>;
}

interface CreateNetworkAdapterOptions {
  client: AxiosInstance;
  resolveAuthHeaders?: () => Promise<NetworkHeaders>;
}

function resolvePathParams(endpoint: string, pathParams: Record<string, string | number>): string {
  return Object.entries(pathParams).reduce((currentEndpoint, [key, value]) => {
    return currentEndpoint.replace(`:${key}`, encodeURIComponent(String(value)));
  }, endpoint);
}

function serializeParams(params: QueryParams): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => appendSearchParam(searchParams, key, entry));
      return;
    }

    appendSearchParam(searchParams, key, value);
  });

  return searchParams.toString();
}

function appendSearchParam(
  searchParams: URLSearchParams,
  key: string,
  value: QueryParamPrimitive,
): void {
  if (value === undefined || value === null) {
    return;
  }

  searchParams.append(key, String(value));
}

export function createNetworkAdapter({
  client,
  resolveAuthHeaders,
}: CreateNetworkAdapterOptions): NetworkAdapter {
  async function buildRequestConfig(
    options: NetworkRequestOptions = {},
    extraConfig: AxiosRequestConfig = {},
  ): Promise<AxiosRequestConfig> {
    const useAuth = options.useAuth ?? true;
    const authHeaders = useAuth && resolveAuthHeaders ? await resolveAuthHeaders() : {};

    return {
      ...extraConfig,
      headers: {
        ...authHeaders,
        ...options.headers,
        ...extraConfig.headers,
      },
      params: options.queryParams,
      paramsSerializer: { serialize: serializeParams },
      signal: options.signal,
      withCredentials: resolveWithCredentials(useAuth, options.withCredentials),
    };
  }

  async function execute<T>(request: () => Promise<AxiosResponse<T>>): Promise<T> {
    try {
      const response = await request();
      return response.data;
    } catch (error) {
      throw normalizeAxiosError(error);
    }
  }

  function resolveEndpoint(
    endpoint: string,
    pathParams: Record<string, string | number> = {},
  ): string {
    return resolvePathParams(endpoint, pathParams);
  }

  return {
    async get<T>(endpoint: string, options?: NetworkRequestOptions): Promise<T> {
      const config = await buildRequestConfig(options);

      return execute(() => client.get<T>(resolveEndpoint(endpoint, options?.pathParams), config));
    },

    async post<T>(endpoint: string, options?: NetworkBodyRequestOptions): Promise<T> {
      const config = await buildRequestConfig(options);

      return execute(() =>
        client.post<T>(resolveEndpoint(endpoint, options?.pathParams), options?.body, config),
      );
    },

    async patch<T>(endpoint: string, options?: NetworkBodyRequestOptions): Promise<T> {
      const config = await buildRequestConfig(options);

      return execute(() =>
        client.patch<T>(resolveEndpoint(endpoint, options?.pathParams), options?.body, config),
      );
    },

    async put<T>(endpoint: string, options?: NetworkBodyRequestOptions): Promise<T> {
      const config = await buildRequestConfig(options);

      return execute(() =>
        client.put<T>(resolveEndpoint(endpoint, options?.pathParams), options?.body, config),
      );
    },

    async delete<T>(endpoint: string, options?: NetworkRequestOptions): Promise<T> {
      const config = await buildRequestConfig(options);

      return execute(() =>
        client.delete<T>(resolveEndpoint(endpoint, options?.pathParams), config),
      );
    },

    async postFormData<T>(endpoint: string, options: NetworkFormRequestOptions): Promise<T> {
      const config = await buildRequestConfig(options);

      return execute(() =>
        client.post<T>(resolveEndpoint(endpoint, options.pathParams), options.formData, config),
      );
    },
  };
}
